/* Every fact the four legal pages state about the business.

   One file, because the same address and the same business name appear on four
   pages and a fifth is coming when the app's own footer grows one. Four copies
   of a postal address is four chances for one of them to be the old one.

   **The nulls are deliberate and a test fails while any of them is null.**
   These are facts I do not have, and a legal page is the worst possible place
   to guess: a wrong governing-law venue is unenforceable and a wrong postal
   address is a false statement to a payment reviewer who is checking exactly
   that. `test/integration/legal_pages_test.rb` names each unset field and
   refuses to let the pages ship carrying a blank.

   The alternative — a `{{PLACEHOLDER}}` rendered to the page, which is what
   `landing/` does — was rejected because it *renders*. It ships, it looks
   deliberate to nobody reading carefully, and nothing catches it. */

export const legal = {
  // "Spruce" is the product name everywhere a reader can see it, and "Spruce
  // AEO" is the business — the party a customer contracts with, the publisher
  // the structured data names, and the name on the Stripe account.
  //
  // **The company's legal name appears nowhere on the site, by the founder's
  // decision of 2026-10-12.** Spruce AEO is its registered trade name and the
  // Stripe account's public business name, so a terms page naming Spruce AEO
  // names the contracting party, and a reviewer comparing the site with the
  // account finds the same name on both. The legal pages named the company
  // until then. `legal_pages_test` holds the site and `llms.txt` to that.
  product: 'Spruce',
  business: 'Spruce AEO',
  domain: 'spruce-aeo.com',

  // One address for everything, settled 2026-09-17. Support, privacy requests
  // and billing questions all land here; three addresses would be three inboxes
  // to watch and one of them would go unread.
  supportEmail: 'hello@spruce-aeo.com',

  // Stripe is the merchant of record via Managed Payments, so Stripe issues the
  // invoice, appears on the statement, and collects the tax. Naming it is not a
  // courtesy — a customer who cannot reconcile the name on their statement with
  // the name on this site files a chargeback, and dispute rate is what Managed
  // Payments eligibility is monitored on.
  mor: 'Stripe',

  // No phone and no support hours, deliberately. Self-serve by default is a
  // standing operating principle — no sales calls, demos or meetings anywhere
  // in the funnel — and a phone number published by a solo founder is a promise
  // to answer it. Email is the honest channel and the only one.
  phone: null,

  // The terms' and the refund policy's date. Moved 2026-10-26 for wording
  // changes to both — where a plan is cancelled, the trial, "no credit card" —
  // none of which changed what either policy says; 2026-10-27 for buying a plan
  // without a trial; and 2026-10-28 for "twelve months for the price of ten".
  effectiveDate: '28 October 2026',

  // The privacy policy's own date, because it has changed since the other
  // three and one shared date would tell a reader the terms changed too. Moved
  // 2026-10-12 for the free-check sections, and 2026-10-26 for a reworded
  // sentence on the right to complain — the page says this date always
  // reflects the current version.
  //
  // **The publishing rule stays on 12 October 2026, not on this date**: a
  // published figure built from free checks uses only checks run on or after
  // the day the page first said they could be published. Moving the date for
  // a wording change must not move that.
  privacyUpdated: '27 October 2026',

  // The registered agent's address, supplied 2026-09-28 — the same one the two
  // trial emails carry as their postal line, so the site and the mail agree
  // rather than giving a reader two addresses for one company.
  //
  // It is a **shared commercial registered-agent address**, which is normal and
  // is what a registered agent is for. It matches public records, which is the
  // thing a payment reviewer checks. Worth knowing rather than worrying about:
  // thousands of entities list this suite, so a reviewer may ask separately for
  // something showing an actual place of business.
  entityAddress: '522 W Riverside Ave, Ste N, Spokane, WA 99201, United States',

  // Confirmed 2026-09-28. Washington because the LLC is organised there. The
  // registered agent sitting in Spokane is *evidence* of that rather than the
  // reason for it — an agent has to be in the state of formation — and getting
  // that backwards is how the agent's location ends up deciding a clause it has
  // nothing to do with.
  governingLawState: 'the State of Washington',

  // **The state, deliberately, and not Spokane County.** Venue is where the
  // company agrees to appear and litigate. Setting it to the county where the
  // registered agent rents an office would commit us to a courthouse chosen by
  // a vendor's street address, in a county the business may have no connection
  // to — and an agent forwards mail, it does not appear for you. Naming the
  // state keeps venue where the governing law already is, and decides nothing
  // that a county would have decided by accident.
  governingLawVenue: 'the State of Washington',
};

/* Which facts a page cannot render without. Read by the test rather than
   re-derived there, so adding a field to the object is what adds it to the
   guard. */
export const REQUIRED = [
  'product', 'business', 'domain', 'supportEmail', 'mor',
  'effectiveDate', 'privacyUpdated', 'entityAddress', 'governingLawState', 'governingLawVenue',
];
